import { ChevronDown, Close, CloseFilled } from "@carbon/icons-react";
import { BORDER_TYPE } from "@Configs/enum";
import { Tooltip } from "antd";
import classNames from "classnames";
import React, { ReactSVGElement, RefObject } from "react";
import { Model } from "react-3layer-common";
import { InputAction } from "../InputText/InputText";
import "./InputTagSelection.scss";

export interface InputTagSelectionProps<T extends Model> {
  /**List value users select*/
  listValue?: T[];
  /**Placeholder of the component*/
  placeHolder?: string;
  /**Not allow to handle change value*/
  disabled?: boolean;
  /**Provide a function to render a specific property as name*/
  render?: (t: T) => string;
  /**Handle the action when click clear value*/
  onClear?: (T: T) => void;
  /**Handle the action when click clear mutiValue*/
  onClearMulti?: () => void;
  /**Handle action on search*/
  onSearch?: (T: string) => void;
  /**Show symbol * as required field*/
  isRequired?: boolean;
  /**Label for current field*/
  label?: string;
  /**Control the style type of component: MATERIAL, BORDERED, FLOAT_LABEL */
  type?: BORDER_TYPE;
  /**border by select case*/
  isSelectOption?: boolean;
  /**Control the size of the component*/
  isSmall?: boolean;
  /**Component enable to search data list*/
  isUsingSearch?: boolean;
  /**Boolean true if used for filter*/
  isFilter?: boolean;
  /**Not expand the title row to see more detail on each post (each row)*/
  isNotExpand?: boolean;
  /**Boolean to set show tooltip */
  isShowTooltip?: boolean;
  /** Provide a custom action (onClick) to the component */
  action?: InputAction;
  /** Custom background color for component: "white" || "gray" */
  bgColor?: "white" | "gray";
  /** Set expand value */
  handlePressExpandedIcon?: () => void;
  /**Clear search value when open */
  clearSearchTerm?: boolean;
  /** Is using in Select */
  isUsingInSelect?: boolean;
  /** Handle the blur event */
  onBlur?: () => void;
  /** Handle the action when the label is clicked */
  handleClickLabel?: () => void;
}

function InputTagSelection(props: InputTagSelectionProps<Model>) {
  const {
    listValue,
    placeHolder,
    disabled,
    onSearch,
    isRequired,
    label = null,
    type = BORDER_TYPE.BORDERED,
    isSmall,
    onClear,
    isUsingSearch,
    isFilter,
    isNotExpand,
    isShowTooltip = false,
    render = defaultRenderObject,
    action,
    bgColor,
    clearSearchTerm,
    handlePressExpandedIcon,
    isUsingInSelect = false,
    onBlur,
    handleClickLabel,
  } = props;

  const internalListValue = React.useMemo<Model[]>(() => {
    return listValue;
  }, [listValue]);

  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const inputRef: RefObject<HTMLInputElement> = React.useRef();
  const inputContainerRef: RefObject<HTMLDivElement> = React.useRef();

  // const handleClearMultiItem = React.useCallback(() => {
  //   if (!disabled) {
  //     if (typeof onClearMulti === "function") {
  //       onClearMulti();
  //     }
  //   }
  // }, [disabled, onClearMulti]);
  const handleClearItem = React.useCallback(
    (item: Model) => {
      if (!disabled) {
        if (typeof onClear === "function") {
          onClear(item);
        }
      }
    },
    [disabled, onClear]
  );

  const handleClearInput = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (event: React.MouseEvent<ReactSVGElement, MouseEvent>) => {
      setSearchTerm("");
      inputRef.current.focus();
      if (typeof onSearch === "function") {
        onSearch(null);
        return;
      }
    },
    [onSearch]
  );

  const handleClickChevron = React.useCallback(
    (event: React.MouseEvent<ReactSVGElement, MouseEvent>) => {
      event.stopPropagation();
      handlePressExpandedIcon();
    },
    [handlePressExpandedIcon]
  );

  React.useEffect(() => {
    if (!clearSearchTerm) {
      setSearchTerm("");
    }
  }, [clearSearchTerm]);

  return (
    <>
      <div className="input-tag-selection__wrapper">
        {(label || action?.name) && (
          <div
            className="input-tag__label m-b--3xs"
            onClick={(event) => {
              handleClickLabel();
              event.stopPropagation();
            }}
          >
            {type !== BORDER_TYPE.FLOAT_LABEL && label && (
              <label
                className={classNames("component__title", {
                  "component__title--disabled": disabled,
                })}
              >
                {label}
                {isRequired && <span className="text-danger">&nbsp;*</span>}
              </label>
            )}
            {action && (
              <span
                className="m-l--3xs body-text--md color-link"
                style={{ cursor: "pointer" }}
                onClick={action.action}
              >
                {action.name}
              </span>
            )}
          </div>
        )}
        <div
          className={classNames("component__input input-tag__container", {
            "input-tag__container--sm": isSmall,
            "input-tag__container--white": bgColor === "white",
            "input-tag__container--material": type === BORDER_TYPE.MATERIAL,
            "input-tag__container--bordered": type === BORDER_TYPE.BORDERED,
            "input-tag--disabled": disabled,
            "input-tag__container--float": type === BORDER_TYPE.FLOAT_LABEL,
            "input-tag--filter-have-item":
              isFilter && internalListValue && internalListValue.length > 0,
          })}
          onClick={() =>
            isUsingSearch
              ? inputRef.current.focus()
              : inputContainerRef.current.focus()
          }
          ref={inputContainerRef}
          onBlur={onBlur}
        >
          {isShowTooltip ? (
            <>
              <Tooltip
                placement="topLeft"
                title={
                  internalListValue?.length ? (
                    <>
                      {internalListValue?.map(
                        (itemValue: any, index: number) => (
                          <React.Fragment
                            key={itemValue?.id ? itemValue?.id : index}
                          >
                            <span>{render(itemValue)}</span>
                            <br />
                          </React.Fragment>
                        )
                      )}
                    </>
                  ) : (
                    ""
                  )
                }
              >
                <div className="input-tag__container-inner-group">
                  {!internalListValue?.length && (
                    <input
                      ref={inputRef}
                      readOnly
                      placeholder={
                        internalListValue.length > 0 ? "" : placeHolder
                      }
                      disabled={disabled}
                    />
                  )}

                  {internalListValue.map((itemValue: Model, index: number) => (
                    <div
                      key={itemValue?.id ? itemValue?.id : index}
                      className={classNames("input-tag-item__label", {
                        "input-tag-item__label--small":
                          type === BORDER_TYPE.BORDERED && isSmall,
                        "p-l--3xs": type === BORDER_TYPE.FLOAT_LABEL && isSmall,
                        "p-l--2xs": !(
                          type === BORDER_TYPE.FLOAT_LABEL && isSmall
                        ),
                      })}
                    >
                      <div className="input-tag-item__text">
                        {render(itemValue)}
                      </div>
                      <div className="icon_close">
                        <Close
                          size={12}
                          className="input-tag-item__icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearItem(itemValue);
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Tooltip>
            </>
          ) : (
            <>
              <div
                className={classNames("input-tag__container-inner", {
                  "input-tag__container-inner-have_search":
                    isUsingSearch && !isUsingInSelect,
                  "using-in-select": isUsingInSelect,
                })}
              >
                <div className="input-tag__container-inner-group">
                  {!internalListValue?.length && (
                    <input
                      ref={inputRef}
                      readOnly
                      placeholder={
                        internalListValue.length > 0 ? "" : placeHolder
                      }
                      disabled={disabled}
                    />
                  )}
                  {internalListValue.map((itemValue: Model, index: number) => (
                    <div
                      key={itemValue?.id ? itemValue?.id : index}
                      className={classNames(
                        "input-tag-item__label m-r--3xs m-b--3xs",
                        {
                          "input-tag-item__label--small":
                            type === BORDER_TYPE.FLOAT_LABEL && true,
                          "p-l--3xs":
                            type === BORDER_TYPE.FLOAT_LABEL && isSmall,
                          "p-l--2xs": !(
                            type === BORDER_TYPE.FLOAT_LABEL && isSmall
                          ),
                        }
                      )}
                    >
                      <div className="input-tag-item__text">
                        {render(itemValue)}
                      </div>
                      {
                        <Close
                          size={12}
                          className="input-tag-item__icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearItem(itemValue);
                          }}
                        />
                      }
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          <div
            id="group-input-icon"
            className={classNames({
              "group-have_search": isUsingSearch && !isUsingInSelect,
            })}
          >
            {type === BORDER_TYPE.FLOAT_LABEL && label && (
              <label
                className={classNames(
                  "component__title component__title--normal",
                  {
                    "component__title--sm": isSmall,
                    "component__title--top": listValue && listValue.length > 0,
                  }
                )}
              >
                {label}
                {isRequired && <span className="text-danger">&nbsp;*</span>}
              </label>
            )}

            {!disabled && searchTerm && (
              <CloseFilled
                size={16}
                className="input-icon input-icon__clear m-x--2xs"
                onClick={handleClearInput}
              />
            )}
            {/* <div> */}
            <ChevronDown
              size={16}
              className={classNames("input-icon", "input-tag__icon", {
                "input-tag__icon--disabled": disabled,
              })}
              onClick={!isNotExpand ? handleClickChevron : undefined}
            />
            {/* </div> */}
          </div>
        </div>
      </div>
    </>
  );
}

function defaultRenderObject<T extends Model>(t: T) {
  return t?.name;
}

export default InputTagSelection;
